import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { submitBooking, fetchBookingCounts } from '../services/api';
import { CLASS_CAPACITY } from '../mock/mock';
import { Calendar, Check, Users } from 'lucide-react';

export default function BookingDialog({ open, onOpenChange, slot, onBooked }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!open || !slot) return;
    setForm({ name: '', email: '', phone: '' });
    const capacity = slot.capacity ?? CLASS_CAPACITY;
    fetchBookingCounts().then((c) => setRemaining(capacity - (c[slot.slotId] || 0))).catch(() => setRemaining(null));
  }, [open, slot]);

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) return;
    if (!form.name || !form.email || !form.phone) { toast.error('Please add your name, mobile and email'); return; }
    setLoading(true);
    try {
      await submitBooking({
        slot_id: slot.slotId,
        day: slot.day,
        time: slot.time,
        class_name: slot.name,
        ...form,
      });
      toast.success('Request sent, Sam will be in touch to confirm your spot');
      onBooked?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Booking failed');
    } finally {
      setLoading(false);
    }
  };

  if (!slot) return null;
  const capacity = slot.capacity ?? CLASS_CAPACITY;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111116] border-white/10 text-white rounded-none max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center brand-gradient"><Calendar className="w-5 h-5 text-white" /></div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-brand">Request Your Spot</div>
              <DialogTitle className="font-display text-3xl uppercase italic">{slot.name}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-white/70">
            {slot.day} · {slot.time} · {slot.level}
          </DialogDescription>
        </DialogHeader>

        {remaining !== null && (
          <div className={`flex items-center gap-2 text-xs uppercase tracking-widest ${remaining <= 3 ? 'text-brand' : 'text-white/70'}`}>
            <Users className="w-3.5 h-3.5" /> {remaining > 0 ? `${remaining} of ${capacity} spots left` : 'Class is full'}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Mobile</label>
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Email</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
          </div>
          <Button disabled={loading || remaining === 0} type="submit" className="w-full brand-gradient text-white rounded-none uppercase tracking-widest text-xs py-6 glow-brand disabled:opacity-60">
            <Check className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send booking request'}
          </Button>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 text-center">Sam will confirm your spot by email or text</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
